import React, { forwardRef } from 'react';
import isNil from 'lodash/isNil';
// eslint-disable-next-line no-unused-vars
import { Switch, SwitchProps } from '@progress/kendo-react-inputs';

/**
 * @typedef {React.PropsWithoutRef<SwitchProps>} TelerikSwitchProps
 *
 * @typedef {Object} AdditionalToggleProps
 * @property {string} [dataTestId] Data test Id.
 *
 * @param {TelerikSwitchProps & AdditionalToggleProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const BaseToggle = ({ readOnly, dataTestId, ...props }, ref) => {
  const {
    onLabel = 'Yes',
    offLabel = 'No',
    checked,
    value,
  } = props;

  const getValue = () => {
    if (isNil(value)) {
      return isNil(checked) ? value : checked;
    }
    return value;
  };

  const realValue = getValue();

  if (readOnly) {
    return (
      <div className='read-only' data-testid={dataTestId}>
        {realValue ? onLabel : offLabel}
      </div>
    );
  }
  return (
    <span data-testid={dataTestId}>
      <Switch
        ref={ref}
        checked={realValue}
        size='small'
        {...props}
      />
    </span>
  );
};

/**
 * A base toggle component with various options and handlers.
 *
 * @param {TelerikSwitchProps & AdditionalToggleProps} props Component props. Extends Telerik Switch's props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Base toggle component.
 */
const ForwardBaseToggle = forwardRef(BaseToggle);

export default ForwardBaseToggle;
